'use client';

import {
  ArrowLeft,
  ArrowRight,
  DoorOpen,
  Move,
  RotateCcw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import Image from 'next/image';
import { useRef, useState } from 'react';
import type { Locale } from '@/i18n/i18n';

const views = ['side', 'front', 'rear', 'interior'] as const;
type View = (typeof views)[number];
const copy = {
  'en-US': {
    title: 'Explore the taxi',
    eyebrow: 'TAKE A LOOK AROUND',
    side: 'Side',
    front: 'Front',
    rear: 'Rear',
    interior: 'Interior',
    alts: [
      'Side view of the yellow Škoda taxi',
      'Front view of the yellow Škoda taxi',
      'Rear view of the yellow Škoda taxi',
      'Passenger cabin and rear seats of the taxi',
    ],
    enter: 'Step inside',
    exit: 'Back outside',
    previous: 'Previous view',
    next: 'Next view',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    reset: 'Reset view',
    hint: 'Tap a marker to explore. Zoom in and drag to look closer.',
    error: 'This photo could not load. Choose another view or try again.',
    retry: 'Try again',
  },
  el: {
    title: 'Γνωρίστε το ταξί',
    eyebrow: 'ΡΙΞΤΕ ΜΙΑ ΜΑΤΙΑ',
    side: 'Πλάγια όψη',
    front: 'Μπροστά',
    rear: 'Πίσω',
    interior: 'Εσωτερικό',
    alts: [
      'Πλάγια όψη του κίτρινου ταξί Škoda',
      'Μπροστινή όψη του κίτρινου ταξί Škoda',
      'Πίσω όψη του κίτρινου ταξί Škoda',
      'Εσωτερικό και πίσω θέσεις επιβατών του ταξί',
    ],
    enter: 'Εσωτερικό',
    exit: 'Επιστροφή έξω',
    previous: 'Προηγούμενη όψη',
    next: 'Επόμενη όψη',
    zoomIn: 'Μεγέθυνση',
    zoomOut: 'Σμίκρυνση',
    reset: 'Επαναφορά όψης',
    hint: 'Πατήστε ένα σημείο για εξερεύνηση. Μεγεθύνετε και σύρετε για να δείτε πιο κοντά.',
    error: 'Η φωτογραφία δεν φορτώθηκε. Επιλέξτε άλλη όψη ή δοκιμάστε ξανά.',
    retry: 'Δοκιμάστε ξανά',
  },
};

export function TaxiTour({ locale }: { locale: Locale }) {
  const t = copy[locale];
  const [view, setView] = useState<View>('side');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const frame = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    pointer: number;
    x: number;
    y: number;
    panX: number;
    panY: number;
  } | null>(null);
  const index = views.indexOf(view);

  function reset() {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    drag.current = null;
  }
  function select(next: View) {
    setView(next);
    setFailed(false);
    reset();
  }
  function step(direction: number) {
    select(views[(index + direction + views.length) % views.length]);
  }
  function changeZoom(direction: number) {
    setZoom(Math.min(2, Math.max(1, zoom + direction * 0.25)));
    setPan({ x: 0, y: 0 });
  }

  return (
    <fieldset
      className="taxi-tour"
      aria-label={t.title}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          step(1);
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          step(-1);
        }
        if (event.key === 'Escape') reset();
      }}
    >
      <div className="tour-heading">
        <p className="eyebrow">{t.eyebrow}</p>
        <span className="tour-count" aria-hidden="true">
          0{index + 1} / 04
        </span>
      </div>
      <div
        className={`tour-frame ${zoom > 1 ? 'is-zoomed' : ''}`}
        ref={frame}
        onPointerDown={(event) => {
          if (
            zoom === 1 ||
            event.button !== 0 ||
            (event.target instanceof Element && event.target.closest('button'))
          )
            return;
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = {
            pointer: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            panX: pan.x,
            panY: pan.y,
          };
        }}
        onPointerMove={(event) => {
          const start = drag.current;
          const bounds = frame.current?.getBoundingClientRect();
          if (!start || start.pointer !== event.pointerId || !bounds) return;
          const limitX = (bounds.width * (zoom - 1)) / 2;
          const limitY = (bounds.height * (zoom - 1)) / 2;
          setPan({
            x: Math.max(
              -limitX,
              Math.min(limitX, start.panX + event.clientX - start.x),
            ),
            y: Math.max(
              -limitY,
              Math.min(limitY, start.panY + event.clientY - start.y),
            ),
          });
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
        onLostPointerCapture={() => {
          drag.current = null;
        }}
      >
        <div
          className="tour-scene"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          }}
        >
          <Image
            key={`${view}-${attempt}`}
            src={`/taxi-tour/${view}.webp`}
            alt={t.alts[index]}
            fill
            sizes="(max-width: 800px) 92vw, 50vw"
            draggable={false}
            onError={() => setFailed(true)}
          />
          {!failed && zoom === 1 && (
            <button
              type="button"
              className="tour-hotspot"
              style={{
                left:
                  view === 'side'
                    ? '23%'
                    : view === 'rear'
                      ? '24%'
                      : view === 'front'
                        ? '50%'
                        : '16%',
                top:
                  view === 'side'
                    ? '30%'
                    : view === 'rear'
                      ? '35%'
                      : view === 'front'
                        ? '24%'
                        : '55%',
              }}
              onClick={() =>
                select(
                  view === 'interior'
                    ? 'side'
                    : view === 'front'
                      ? 'side'
                      : 'interior',
                )
              }
            >
              <span className="tour-hotspot-icon">
                {view === 'front' || view === 'interior' ? (
                  <ArrowLeft size={20} aria-hidden="true" />
                ) : (
                  <DoorOpen size={20} aria-hidden="true" />
                )}
              </span>
              <span className="tour-hotspot-label">
                {view === 'interior'
                  ? t.exit
                  : view === 'front'
                    ? t.side
                    : t.enter}
              </span>
            </button>
          )}
        </div>
        {failed && (
          <div className="tour-error" role="alert">
            <p>{t.error}</p>
            <button
              type="button"
              onClick={() => {
                setFailed(false);
                setAttempt(attempt + 1);
              }}
            >
              {t.retry}
            </button>
          </div>
        )}
        <div className="tour-tools">
          <button
            type="button"
            aria-label={t.zoomOut}
            disabled={zoom === 1}
            onClick={() => changeZoom(-1)}
          >
            <ZoomOut size={20} aria-hidden="true" />
          </button>
          <span className="tour-zoom" aria-live="polite">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            aria-label={t.zoomIn}
            disabled={zoom === 2}
            onClick={() => changeZoom(1)}
          >
            <ZoomIn size={20} aria-hidden="true" />
          </button>
          <button type="button" aria-label={t.reset} onClick={reset}>
            <RotateCcw size={18} aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="tour-navigation">
        <button
          type="button"
          className="tour-arrow"
          aria-label={t.previous}
          onClick={() => step(-1)}
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <div className="tour-views">
          {views.map((scene) => (
            <button
              type="button"
              key={scene}
              aria-pressed={view === scene}
              onClick={() => select(scene)}
            >
              {t[scene]}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="tour-arrow"
          aria-label={t.next}
          onClick={() => step(1)}
        >
          <ArrowRight size={20} aria-hidden="true" />
        </button>
      </div>
      <p className="tour-hint">
        <Move size={16} aria-hidden="true" />
        {t.hint}
      </p>
      <p className="tour-sr-only" aria-live="polite" aria-atomic="true">
        {t[view]}
      </p>
    </fieldset>
  );
}
